import React, { useEffect, useMemo, useState } from 'react';
import { isAfter, isBefore } from 'date-fns';
import PropTypes from 'prop-types';

import {
  convertToAppTime,
  getAppDateString,
  toAppTime,
  utcToAppTime,
} from '@/lib/date';
import CallCentreWorkingStatusContext from './CallCentreWorkingStatusContext';

const isCallCentreOpen = (now) => {
  const currentAppTime = convertToAppTime(now);
  const today = getAppDateString(currentAppTime);
  const isWeekend =
    utcToAppTime(today).getDay() === 0 || utcToAppTime(today).getDay() === 6;
  const openTime = isWeekend
    ? toAppTime(`${today}T08:30:00`)
    : toAppTime(`${today}T08:00:00`);
  const closeTime = isWeekend
    ? toAppTime(`${today}T17:30:00`)
    : toAppTime(`${today}T20:00:00`);

  return (
    isAfter(currentAppTime, openTime) && isBefore(currentAppTime, closeTime)
  );
};

const isLiveChatAvailable = (now) => {
  const currentAppTime = convertToAppTime(now);
  const today = getAppDateString(currentAppTime);
  const openTime = toAppTime(`${today}T08:30:00`);
  const closeTime = toAppTime(`${today}T17:30:00`);

  return (
    isAfter(currentAppTime, openTime) && isBefore(currentAppTime, closeTime)
  );
};

/**
 * @param {{ children: React.ReactNode }} props
 */
const CallCentreWorkingStatusProvider = ({
  interval = 30 * 1000,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(isCallCentreOpen(new Date()));
  const [isLiveChatOn, setIsLiveChatOn] = useState(
    isLiveChatAvailable(new Date()),
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const isCCOpen = isCallCentreOpen(new Date());
      const isLCOn = isLiveChatAvailable(new Date());
      setIsOpen(isCCOpen);
      setIsLiveChatOn(isLCOn);
    }, interval);
    return () => clearInterval(timer);
  }, [interval, isOpen]);

  const value = useMemo(
    () => ({
      isOpen,
      isLiveChatOn,
    }),
    [isOpen, isLiveChatOn],
  );

  return (
    <CallCentreWorkingStatusContext.Provider value={value}>
      {children}
    </CallCentreWorkingStatusContext.Provider>
  );
};

CallCentreWorkingStatusProvider.propTypes = {
  interval: PropTypes.number,
  children: PropTypes.node.isRequired,
};

export default CallCentreWorkingStatusProvider;
